import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, Cell } from "recharts";
import jsPDF from "jspdf";
import { Money, CreditCard, FilePdf, TrendUp, X as XIcon, Star, Receipt } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function RootDashboard() {
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = (d) => {
    setLoading(true);
    api.get("/reports/daily", { params: { date: d } })
      .then((r) => setReport(r.data))
      .catch(() => toast.error("Error cargando reporte"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(date); }, [date]);

  const generatePDF = () => {
    if (!report) return;
    const doc = new jsPDF({ unit: "pt", format: "letter" });
    const w = doc.internal.pageSize.getWidth();
    let y = 50;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(20);
    doc.setTextColor(211, 47, 47);
    doc.text("J.R. TACOS EL PAISA", w / 2, y, { align: "center" });
    y += 22;

    doc.setFontSize(13);
    doc.setTextColor(60, 60, 60);
    doc.setFont("helvetica", "normal");
    doc.text("Corte Diario de Caja", w / 2, y, { align: "center" });
    y += 18;
    doc.setFontSize(10);
    doc.text(`Fecha: ${report.date}`, w / 2, y, { align: "center" });
    y += 30;

    // Resumen
    doc.setDrawColor(220, 220, 220);
    doc.line(50, y, w - 50, y); y += 20;
    doc.setFontSize(11); doc.setFont("helvetica", "bold"); doc.setTextColor(0,0,0);
    doc.text("RESUMEN", 50, y); y += 18;
    doc.setFont("helvetica", "normal");
    const rows = [
      ["Ventas Brutas", `$${report.total_brutas.toFixed(2)}`],
      ["Efectivo", `$${report.total_efectivo.toFixed(2)}`],
      ["Tarjeta", `$${report.total_tarjeta.toFixed(2)}`],
      ["Órdenes Pagadas", String(report.n_orders_paid)],
      ["Órdenes Canceladas", String(report.n_orders_cancelled)],
    ];
    rows.forEach(([k, v]) => {
      doc.setTextColor(80, 80, 80); doc.text(k, 60, y);
      doc.setTextColor(0, 0, 0); doc.setFont("helvetica", "bold"); doc.text(v, w - 60, y, { align: "right" });
      doc.setFont("helvetica", "normal");
      y += 16;
    });

    // Estrella
    y += 10; doc.line(50, y, w - 50, y); y += 20;
    doc.setFont("helvetica", "bold"); doc.text("PRODUCTO ESTRELLA", 50, y); y += 18;
    doc.setFont("helvetica", "normal");
    if (report.estrella?.name) {
      doc.text(`${report.estrella.name} · ${report.estrella.qty} vendidos`, 60, y);
    } else {
      doc.text("—", 60, y);
    }
    y += 24;

    // Top
    if (report.top_products?.length) {
      doc.line(50, y, w - 50, y); y += 20;
      doc.setFont("helvetica", "bold"); doc.text("TOP 5 PRODUCTOS", 50, y); y += 18;
      doc.setFont("helvetica", "normal");
      report.top_products.forEach((p) => {
        doc.text(`${p.qty}× ${p.name}`, 60, y);
        doc.text(`$${p.revenue.toFixed(2)}`, w - 60, y, { align: "right" });
        y += 16;
      });
    }

    // Cancelaciones
    if (report.cancellations?.length) {
      y += 10; doc.line(50, y, w - 50, y); y += 20;
      doc.setFont("helvetica", "bold"); doc.text("CANCELACIONES", 50, y); y += 18;
      doc.setFont("helvetica", "normal");
      report.cancellations.forEach((c) => {
        doc.text(`#${String(c.order_number || 0).padStart(3, "0")} · Mesa ${c.table} · $${c.total.toFixed(2)}`, 60, y);
        doc.text(c.reason || "—", 280, y);
        y += 16;
      });
    }

    y += 40;
    doc.setFontSize(9);
    doc.setTextColor(150, 150, 150);
    doc.text("Generado por Taquería 360 · " + new Date().toLocaleString("es-MX"), w / 2, y, { align: "center" });

    doc.save(`corte_${report.date}.pdf`);
    toast.success("PDF generado");
  };

  return (
    <div className="p-6 md:p-8">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-zinc-500 font-semibold mb-2">Panel del Administrador</div>
          <h1 className="font-display text-3xl md:text-4xl font-black tracking-tight">Resumen del día</h1>
        </div>
        <div className="flex items-center gap-2">
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="root-date" className="h-12 px-4 rounded-xl border-2 border-zinc-200 focus:border-brand outline-none font-medium" />
          <button onClick={generatePDF} disabled={!report} data-testid="root-generate-pdf" className="btn-brand h-12 px-5 inline-flex items-center gap-2 disabled:opacity-50">
            <FilePdf size={20} weight="bold" /> Generar Corte PDF
          </button>
        </div>
      </div>

      {loading && <div className="text-zinc-500">Cargando...</div>}

      {report && (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-2xl border border-zinc-200 p-5">
              <div className="flex items-center gap-3 text-zinc-500 mb-3"><TrendUp size={20} weight="bold" /><span className="text-xs uppercase tracking-wider font-semibold">Ventas Brutas</span></div>
              <div className="font-display text-3xl md:text-4xl font-black">${report.total_brutas.toFixed(2)}</div>
              <div className="text-sm text-zinc-500 mt-1">{report.n_orders_paid} órdenes pagadas</div>
            </div>
            <div className="bg-white rounded-2xl border border-zinc-200 p-5">
              <div className="flex items-center gap-3 text-emerald-600 mb-3"><Money size={20} weight="bold" /><span className="text-xs uppercase tracking-wider font-semibold">Efectivo</span></div>
              <div className="font-display text-3xl md:text-4xl font-black">${report.total_efectivo.toFixed(2)}</div>
              <div className="text-sm text-zinc-500 mt-1">
                {report.total_brutas > 0 ? Math.round((report.total_efectivo / report.total_brutas) * 100) : 0}% del total
              </div>
            </div>
            <div className="bg-white rounded-2xl border border-zinc-200 p-5">
              <div className="flex items-center gap-3 text-zinc-900 mb-3"><CreditCard size={20} weight="bold" /><span className="text-xs uppercase tracking-wider font-semibold">Tarjeta</span></div>
              <div className="font-display text-3xl md:text-4xl font-black">${report.total_tarjeta.toFixed(2)}</div>
              <div className="text-sm text-zinc-500 mt-1">
                {report.total_brutas > 0 ? Math.round((report.total_tarjeta / report.total_brutas) * 100) : 0}% del total
              </div>
            </div>
            <div className="bg-white rounded-2xl border border-zinc-200 p-5">
              <div className="flex items-center gap-3 text-red-600 mb-3"><XIcon size={20} weight="bold" /><span className="text-xs uppercase tracking-wider font-semibold">Canceladas</span></div>
              <div className="font-display text-3xl md:text-4xl font-black">{report.n_orders_cancelled}</div>
              <div className="text-sm text-zinc-500 mt-1">órdenes</div>
            </div>
          </div>

          {/* Chart + Top products */}
          <div className="grid lg:grid-cols-3 gap-4 mb-6">
            <div className="lg:col-span-2 bg-white rounded-2xl border border-zinc-200 p-5">
              <div className="text-xs uppercase tracking-wider font-semibold text-zinc-500 mb-4">Ventas por hora</div>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={report.hourly_sales}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f4f4f5" />
                  <XAxis dataKey="hour" tickFormatter={(h) => `${h}h`} stroke="#a1a1aa" fontSize={11} />
                  <YAxis stroke="#a1a1aa" fontSize={11} />
                  <Tooltip formatter={(v) => `$${Number(v).toFixed(2)}`} labelFormatter={(h) => `Hora ${h}:00`} cursor={{ fill: "rgba(211,47,47,0.06)" }} />
                  <Bar dataKey="total" radius={[6, 6, 0, 0]}>
                    {report.hourly_sales.map((_, i) => <Cell key={i} fill="#D32F2F" />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="bg-white rounded-2xl border border-zinc-200 p-5">
              <div className="flex items-center gap-2 mb-4">
                <Star size={18} weight="fill" className="text-orange-500" />
                <div className="text-xs uppercase tracking-wider font-semibold text-zinc-500">Estrella del día</div>
              </div>
              {report.estrella?.name ? (
                <div>
                  <div className="font-display text-2xl font-black leading-tight">{report.estrella.name}</div>
                  <div className="text-zinc-500 text-sm mt-1">{report.estrella.qty} unidades vendidas</div>
                </div>
              ) : (
                <div className="text-zinc-400 text-sm">Sin ventas</div>
              )}

              <div className="mt-6 text-xs uppercase tracking-wider font-semibold text-zinc-500 mb-3">Top 5</div>
              <div className="space-y-2">
                {report.top_products.length === 0 && <div className="text-zinc-400 text-sm">Sin datos</div>}
                {report.top_products.map((p, i) => (
                  <div key={i} className="flex justify-between items-center text-sm">
                    <span className="font-semibold truncate">{i + 1}. {p.name}</span>
                    <span className="text-brand font-bold">{p.qty}×</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Cancellations */}
          {report.cancellations.length > 0 && (
            <div className="bg-white rounded-2xl border border-zinc-200 p-5">
              <div className="flex items-center gap-2 mb-4">
                <Receipt size={18} weight="bold" className="text-red-600" />
                <div className="text-xs uppercase tracking-wider font-semibold text-zinc-500">Cancelaciones del día</div>
              </div>
              <div className="space-y-2">
                {report.cancellations.map((c) => (
                  <div key={c.id} className="flex justify-between items-center bg-red-50 rounded-xl px-4 py-3">
                    <div>
                      <div className="font-bold text-sm">Orden #{String(c.order_number || 0).padStart(3, "0")} · Mesa {c.table}</div>
                      <div className="text-xs text-zinc-600 mt-0.5">{c.reason || "Sin motivo"}</div>
                    </div>
                    <div className="font-bold text-red-700">${c.total.toFixed(2)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
